import type { AxiosInstance } from "axios";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { isValidId } from "../u2000Core/tools/U2000IdUtils";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";
import { U2000FormStateEnumEditMode } from "../u2000Core/reducer/formReducer/U2000FormState";
import { u3155ResolveFamilyLabel, type U3155FamilyItem } from "../u3155MinutesWorkNature/U3155Service";

const LOGICALUNIT_URL = "U3139";

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
  INITCREATEMINUTE: string;
  FINDCADASTRES: string;
  FINDSEARCHCADASTRES: string;
  FINDEMPLOYEE: string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id) => `${LOGICALUNIT_URL}/${id}/audits`,
  INITCREATEMINUTE: `${LOGICALUNIT_URL}/initCreateMinute`,
  FINDCADASTRES: `${LOGICALUNIT_URL}/findCadastres`,
  FINDSEARCHCADASTRES: `${LOGICALUNIT_URL}/findSearchCadastres`,
  FINDEMPLOYEE: `${LOGICALUNIT_URL}/findEmployee`,
};

export interface U3139WorkNatureTranslation {
  id?: string;
  workNatureId?: string;
  languageCode?: string;
  workNatureLabel?: string;
  version?: number;
}

export type U3139FamilyItem = U3155FamilyItem;

export interface U3139WorkNatureItem {
  id?: string;
  workNatureCode?: string;
  active?: boolean;
  version?: number;
  translations?: U3139WorkNatureTranslation[];
  family?: U3139FamilyItem | null;
}

export interface U3139SecondaryWorkNatureLine {
  id?: string;
  sortOrder?: number;
  version?: number;
  workNature: U3139WorkNatureItem;
}

export interface U3139InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  /** Référentiel actif pour le combo (prioritaire). */
  workNatures?: U3139WorkNatureItem[];
  /** Alias backend — même contenu que workNatures. */
  rfWorkNature?: U3139WorkNatureItem[];
  rfWorkNatureTranslation?: U3139WorkNatureTranslation[];
  /** Familles actives (BL-0694). */
  families?: U3139FamilyItem[];
}

/** Normalise la réponse GET /U3139/initLU (workNatures ou rfWorkNature). */
export function normalizeU3139InitLU(raw: U3139InitLU): U3139InitLU {
  const catalog =
    raw.workNatures?.length ? raw.workNatures : raw.rfWorkNature?.length ? raw.rfWorkNature : [];
  return {
    launchSearch: raw.launchSearch ?? true,
    hideSearch: raw.hideSearch ?? false,
    nbRecordPerPage: raw.nbRecordPerPage ?? 25,
    workNatures: catalog.filter((wn) => wn.active !== false),
    rfWorkNature: raw.rfWorkNature,
    rfWorkNatureTranslation: raw.rfWorkNatureTranslation,
    families: raw.families ?? [],
  };
}

export function u3139FormatFamilyLabel(
  family: U3139FamilyItem | null | undefined,
  languageCode: string,
): string {
  return u3155ResolveFamilyLabel(family, languageCode);
}

/** Affichage historique T0008 : « libellé (code) ». */
export function u3139FormatLegacyWorkNatureDisplay(
  legacyWorkNatureLabel?: string | null,
  legacyWorkNatureCode?: string | null,
): string {
  const label = legacyWorkNatureLabel?.trim() ?? "";
  const code = legacyWorkNatureCode?.trim() ?? "";
  if (label && code) return `${label} (${code})`;
  return label || code;
}

export function u3139ResolveWorkNatureId(
  workNature: U3139WorkNatureItem | string | null | undefined,
): string | undefined {
  if (workNature == null || workNature === "") return undefined;
  if (typeof workNature === "string") return workNature.trim() || undefined;
  return workNature.id?.trim() || undefined;
}

/** Aligne workNature sur le référentiel initLU (id + libellés) après GET ou navigation formulaire. */
export function u3139SyncWorkNatureFromCatalog(
  workNature: U3139WorkNatureItem | string | null | undefined,
  workNatures?: U3139WorkNatureItem[],
): U3139WorkNatureItem | null {
  const id = u3139ResolveWorkNatureId(workNature);
  if (!id) return null;
  const fromCatalog = workNatures?.find((w) => w.id === id);
  if (fromCatalog) {
    return {
      ...fromCatalog,
      ...(typeof workNature === "object" && workNature != null ? workNature : {}),
      id,
      translations: fromCatalog.translations?.length
        ? fromCatalog.translations
        : typeof workNature === "object" && workNature != null
          ? workNature.translations
          : undefined,
    };
  }
  return typeof workNature === "object" && workNature != null ? { ...workNature, id } : { id };
}

/**
 * Fusionne le catalogue initLU avec la nature sélectionnée (GET) si son id n’y figure pas
 * (ex. initLU chargé avant T0008) — permet à la combo d’afficher le libellé API.
 */
export function u3139MergeWorkNatureCatalogWithSelection(
  catalog: U3139WorkNatureItem[] | undefined,
  selected: U3139WorkNatureItem | string | null | undefined,
): U3139WorkNatureItem[] {
  const base = catalog?.length ? [...catalog] : [];
  const selectedId = u3139ResolveWorkNatureId(selected);
  if (!selectedId || base.some((w) => w.id === selectedId)) {
    return base;
  }
  if (typeof selected === "object" && selected != null) {
    return [...base, { ...selected, id: selectedId }];
  }
  return [...base, { id: selectedId }];
}

/** Aligne les natures secondaires sur le référentiel initLU (BL-0694). */
export function u3139SyncSecondaryWorkNaturesFromCatalog(
  lines: U3139SecondaryWorkNatureLine[] | undefined,
  workNatures?: U3139WorkNatureItem[],
): U3139SecondaryWorkNatureLine[] {
  if (!lines?.length) return [];
  return lines
    .map((line) => {
      const synced = u3139SyncWorkNatureFromCatalog(line.workNature, workNatures);
      if (!synced?.id) return null;
      return { ...line, workNature: synced };
    })
    .filter((line): line is U3139SecondaryWorkNatureLine => line != null);
}

export function u3139CollectSecondaryWorkNatureIds(lines: U3139SecondaryWorkNatureLine[] | undefined): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const line of lines ?? []) {
    const id = u3139ResolveWorkNatureId(line.workNature);
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

export interface U3139ProjectItem {
  id?: string;
  projetNumber?: string;
  projetName?: string;
}

export interface U3139EmployeeItem {
  id?: string;
  codeEmployee?: string;
  firstName?: string;
  lastName?: string;
}

export interface U3139DistrictItem {
  id?: string;
  codeDistrict?: string;
  propertyDistrict?: string;
}

/** Référence master rf_lot_detail (U3142) — lecture GET, non envoyé en create/update. */
export interface U3139LotDetailRef {
  id?: string;
}

export interface U3139CadastreItem {
  id?: string;
  cadastreCode?: string;
  cadastre?: string;
  rankCode?: string;
  rankLabel?: string | null;
  rank?: string;
}

export interface U3139DetailLot {
  id?: string;
  version?: number;
  initialLot?: string;
  /** Rang saisi sur la ligne minute (legacy), distinct du rang cadastre. */
  rankLabel?: string | null;
  createdLot?: string | null;
  coordinateX?: number | null;
  coordinateY?: number | null;
  renovationMandate?: number | null;
  partieDeLot?: boolean;
  /** Master rf_lot_detail (U3142) si lot_detail_id lié — lecture GET uniquement. */
  lotDetail?: U3139LotDetailRef | null;
  district?: U3139DistrictItem | null;
  cadastre?: U3139CadastreItem | null;
  idChecked?: boolean;
}

export function u3139FormatDistrictLabel(district: U3139DistrictItem | null | undefined): string {
  if (!district) return "";
  return district.propertyDistrict?.trim() ?? "";
}

export interface U3139Minutes {
  id: string;
  version?: number;
  minuteNumber?: string;
  clientName?: string;
  minuteDate?: string | null;
  streetAddress?: string | null;
  planNumber?: string | null;
  classification?: string | null;
  notebookNumber?: string | null;
  pageNumber?: string | null;
  remark?: string | null;
  isRenovation: boolean;
  isInactive: boolean;
  workNature?: U3139WorkNatureItem | null;
  legacyWorkNatureCode?: string | null;
  legacyWorkNatureLabel?: string | null;
  secondaryWorkNatures?: U3139SecondaryWorkNatureLine[];
  project?: U3139ProjectItem | null;
  surveyor?: U3139EmployeeItem | null;
  detailLots?: U3139DetailLot[];
}

export type U3139MinutesSearchRow = U3139Minutes;

export type U3139MinutesFormRow = U3139Minutes;

export interface U3139InitCreateMinuteRequest {
  projetNumber: string;
}

export interface U3139InitCreateMinute {
  project?: U3139ProjectItem | null;
  customer?: { id?: string; customerCode?: string; customerName?: string } | null;
  clientName?: string;
  streetAddress?: string | null;
  surveyor?: U3139EmployeeItem | null;
  minuteDate?: string | null;
  detailLots?: U3139DetailLot[];
  isRenovation?: boolean;
  isInactive?: boolean;
}

export interface U3139FindCadastreItem {
  id: string;
  cadastreCode?: string;
  cadastre?: string;
  rankCode?: string;
  rankLabel?: string;
  district?: U3139DistrictItem | null;
}

/** Item autocomplete critère liste — POST /U3139/findSearchCadastres (BL-0102). */
export interface U3139FindSearchCadastreItem {
  id: string;
  cadastreCode?: string;
  cadastre?: string;
  rankCode?: string;
  rankLabel?: string;
  district?: U3139DistrictItem | null;
}

type U3139FindCadastreLike = U3139FindCadastreItem | U3139FindSearchCadastreItem | Record<string, unknown>;

/** Construit l'objet cadastre ligne lot à partir d'un résultat findCadastres / findSearchCadastres. */
export function u3139BuildCadastreFromFindItem(item: U3139FindCadastreLike | null | undefined): U3139CadastreItem | null {
  if (!item || typeof item !== "object") return null;
  const rec = item as Record<string, unknown>;
  const id = String(rec.id ?? "").trim();
  if (!id) return null;
  const rankCode = String(rec.rankCode ?? rec.rank ?? "").trim() || undefined;
  const rankLabelRaw = rec.rankLabel;
  const rankLabel =
    rankLabelRaw == null || rankLabelRaw === "" ? null : String(rankLabelRaw).trim() || null;
  return {
    id,
    cadastreCode: rec.cadastreCode != null ? String(rec.cadastreCode) : undefined,
    cadastre: rec.cadastre != null ? String(rec.cadastre) : undefined,
    rankCode,
    rankLabel,
    rank: rankCode,
  };
}

/** Champs formulaire lot après sélection autocomplete district. */
export function u3139ExtractDetailLotDistrictFields(
  item: { id?: string; codeDistrict?: string; propertyDistrict?: string } | null,
): { fieldName: string; value: unknown }[] {
  if (item?.id?.trim()) {
    return [
      {
        fieldName: 'district',
        value: {
          id: item.id.trim(),
          codeDistrict: item.codeDistrict ?? null,
          propertyDistrict: item.propertyDistrict ?? null,
        },
      },
      { fieldName: 'cadastre', value: null },
    ];
  }
  return [
    { fieldName: 'district', value: null },
    { fieldName: 'cadastre', value: null },
  ];
}

/** Champs formulaire lot après sélection autocomplete cadastre (objet cadastre complet avec rang). */
export function u3139ExtractDetailLotCadastreFields(
  item: U3139FindCadastreLike | null,
): { fieldName: string; value: unknown }[] {
  if (item && typeof item === "object") {
    const district = (item as U3139FindCadastreItem).district;
    const fields: { fieldName: string; value: unknown }[] = [
      { fieldName: "cadastre", value: u3139BuildCadastreFromFindItem(item) },
    ];
    // findCadastres (grille) n'inclut pas toujours district — ne pas effacer le district déjà saisi.
    if (district?.id?.trim()) {
      fields.push({
        fieldName: "district",
        value: {
          id: district.id.trim(),
          codeDistrict: district.codeDistrict ?? null,
          propertyDistrict: district.propertyDistrict ?? null,
        },
      });
    }
    return fields;
  }
  return [{ fieldName: "cadastre", value: null }];
}

/** Rang « A » = défaut Cadastre du Québec — masqué à l'affichage. */
export function u3139IsDefaultCadastreRank(rankCode?: string | null, rankLabel?: string | null): boolean {
  const code = rankCode?.trim().toUpperCase() ?? "";
  const label = rankLabel?.trim().toUpperCase() ?? "";
  return code === "A" || label === "A";
}

/** Rang visible (colonne Rang, dropdown) — vide si rang A. */
export function u3139FormatCadastreRankDisplay(rankCode?: string | null, rankLabel?: string | null): string {
  if (u3139IsDefaultCadastreRank(rankCode, rankLabel)) return "";
  return rankLabel?.trim() || rankCode?.trim() || "";
}

/** Libellé cellule grille — nom cadastre seul (sans rang). */
export function u3139FormatDetailLotCadastreLabel(lot: U3139DetailLot | null | undefined): string {
  return lot?.cadastre?.cadastre?.trim() ?? "";
}

/** Rang affiché en colonne « Rang (ligne) » — rankLabel ligne, sinon rang cadastre ; masqué si A. */
export function u3139ResolveDetailLotRankLabel(lot: U3139DetailLot | null | undefined): string {
  const lineRank = lot?.rankLabel?.trim();
  if (lineRank) {
    return u3139FormatCadastreRankDisplay(lineRank, lineRank);
  }
  return u3139FormatCadastreRankDisplay(lot?.cadastre?.rankCode, lot?.cadastre?.rankLabel);
}

/** Libellé autocomplete / champ cadastre — nom seul si rang A, sinon nom (rang) pour désambiguïser. */
export function u3139FormatFindCadastreLabel(item: U3139FindCadastreLike | null | undefined): string {
  if (!item || typeof item !== "object") return "";
  const cadastre = u3139BuildCadastreFromFindItem(item);
  if (!cadastre) return "";
  const name = cadastre.cadastre?.trim() ?? "";
  const rankDisplay = u3139FormatCadastreRankDisplay(cadastre.rankCode, cadastre.rankLabel);
  return rankDisplay ? `${name} (${rankDisplay})` : name;
}

export interface U3139FindEmployeeItem {
  id: string;
  codeEmployee?: string;
  firstName?: string;
  lastName?: string;
  isActive?: boolean;
}

/** Meta critère recherche liste — inclut employés inactifs (BL-0113). */
export const U3139_FIND_EMPLOYEE_INCLUDE_INACTIVE_CRITERION = {
  fieldName: 'includeInactive',
  value: true,
} as const;

export const emptyMinutes = (): U3139MinutesFormRow => ({
  id: "",
  minuteNumber: "",
  clientName: "",
  minuteDate: null,
  streetAddress: null,
  planNumber: null,
  classification: null,
  notebookNumber: null,
  pageNumber: null,
  remark: null,
  isRenovation: false,
  isInactive: false,
  workNature: null,
  legacyWorkNatureCode: null,
  legacyWorkNatureLabel: null,
  secondaryWorkNatures: [],
  project: null,
  surveyor: null,
  detailLots: [],
});

export const emptyDetailLot = (): U3139DetailLot => ({
  id: "",
  initialLot: "",
  district: null,
  cadastre: null,
  createdLot: null,
  coordinateX: null,
  coordinateY: null,
  renovationMandate: null,
});

/** « Famille — libellé nature » pour grilles (U3121 onglet Minutes, etc.). */
export function u3139FormatWorkNatureWithFamilyLabel(
  workNature: U3139WorkNatureItem | string | null | undefined,
  languageCode: string,
  workNatures?: U3139WorkNatureItem[],
): string {
  if (!workNature) return "";
  const item = typeof workNature === "object" ? workNature : undefined;
  const familyLabel = u3139FormatFamilyLabel(item?.family, languageCode);
  const natureLabel = u3139ResolveWorkNatureLabel(workNature, languageCode, workNatures);
  if (familyLabel && natureLabel) return `${familyLabel} — ${natureLabel}`;
  return natureLabel || familyLabel;
}

export function u3139ResolveWorkNatureLabel(
  workNature: U3139WorkNatureItem | string | null | undefined,
  languageCode: string,
  workNatures?: U3139WorkNatureItem[],
): string {
  if (!workNature) return "";
  const id = typeof workNature === "string" ? workNature.trim() : workNature.id?.trim();
  const catalogItem = id ? workNatures?.find((w) => w.id === id) : undefined;
  const item = typeof workNature === "string" ? catalogItem : workNature;
  if (!item && !catalogItem) return id ?? "";
  const resolved = item ?? catalogItem!;
  const code = resolved.workNatureCode?.trim() ?? catalogItem?.workNatureCode?.trim() ?? "";
  const translations = resolved.translations ?? catalogItem?.translations;
  const tr =
    translations?.find((t) => t.languageCode === languageCode) ??
    translations?.find((t) => t.languageCode === "fr");
  return tr?.workNatureLabel?.trim() || code;
}

export function u3139FormatWorkNatureOptionLabel(
  workNature: U3139WorkNatureItem,
  languageCode: string,
  workNatures?: U3139WorkNatureItem[],
): string {
  const label = u3139ResolveWorkNatureLabel(workNature, languageCode, workNatures);
  const code = workNature.workNatureCode?.trim();
  return code && label ? `${label} (${code})` : label || code || "";
}

export function mapInitPreviewToFormRow(preview: U3139InitCreateMinute): U3139MinutesFormRow {
  return {
    ...emptyMinutes(),
    clientName: preview.clientName?.trim() ?? "",
    minuteDate: preview.minuteDate ?? null,
    streetAddress: preview.streetAddress ?? null,
    isRenovation: preview.isRenovation ?? false,
    isInactive: preview.isInactive ?? false,
    project: preview.project ?? null,
    surveyor: preview.surveyor ?? null,
    detailLots: (preview.detailLots ?? []).map((lot) => ({
      ...emptyDetailLot(),
      initialLot: lot.initialLot ?? "",
      district: lot.district ?? null,
      cadastre: lot.cadastre ?? null,
      createdLot: lot.createdLot ?? null,
      coordinateX: lot.coordinateX ?? null,
      coordinateY: lot.coordinateY ?? null,
      renovationMandate: lot.renovationMandate ?? null,
    })),
  };
}

function mapSecondaryWorkNatureLinesToPayload(lines: U3139SecondaryWorkNatureLine[] | undefined) {
  return (lines ?? [])
    .map((line) => {
      const workNatureId = u3139ResolveWorkNatureId(line.workNature);
      if (!workNatureId) return null;
      const base = { workNature: { id: workNatureId } };
      if (line.id && isValidId(line.id)) {
        return {
          ...base,
          id: line.id,
          ...(typeof line.version === "number" ? { version: line.version } : {}),
          ...(typeof line.sortOrder === "number" ? { sortOrder: line.sortOrder } : {}),
        };
      }
      return base;
    })
    .filter((line): line is NonNullable<typeof line> => line != null);
}

function mapDetailLotLineToPayload(lot: U3139DetailLot) {
  const districtId = lot.district?.id?.trim();
  return {
    initialLot: lot.initialLot?.trim() ?? "",
    rankLabel: lot.rankLabel ?? null,
    createdLot: lot.createdLot ?? null,
    coordinateX: lot.coordinateX ?? null,
    coordinateY: lot.coordinateY ?? null,
    renovationMandate: lot.renovationMandate ?? null,
    cadastre: lot.cadastre?.id?.trim() ? { id: lot.cadastre.id.trim() } : null,
    ...(districtId ? { district: { id: districtId } } : {}),
  };
}

export function mapFormRowToCreatePayload(row: U3139MinutesFormRow) {
  return {
    minuteNumber: row.minuteNumber?.trim() ?? "",
    clientName: row.clientName?.trim() ?? "",
    workNature: { id: u3139ResolveWorkNatureId(row.workNature)! },
    minuteDate: row.minuteDate ?? undefined,
    streetAddress: row.streetAddress ?? null,
    planNumber: row.planNumber ?? null,
    classification: row.classification ?? null,
    notebookNumber: row.notebookNumber ?? null,
    pageNumber: row.pageNumber ?? null,
    remark: row.remark ?? null,
    isRenovation: row.isRenovation,
    isInactive: row.isInactive,
    project: row.project?.id ? { id: row.project.id } : undefined,
    surveyor: row.surveyor?.id ? { id: row.surveyor.id } : undefined,
    detailLots: (row.detailLots ?? []).map(mapDetailLotLineToPayload),
    ...(row.secondaryWorkNatures?.length
      ? { secondaryWorkNatures: mapSecondaryWorkNatureLinesToPayload(row.secondaryWorkNatures) }
      : {}),
  };
}

export function mapFormRowToUpdatePayload(row: U3139MinutesFormRow) {
  return {
    id: row.id,
    version: row.version,
    clientName: row.clientName?.trim() ?? "",
    workNature: { id: u3139ResolveWorkNatureId(row.workNature)! },
    minuteDate: row.minuteDate ?? undefined,
    streetAddress: row.streetAddress ?? null,
    planNumber: row.planNumber ?? null,
    classification: row.classification ?? null,
    notebookNumber: row.notebookNumber ?? null,
    pageNumber: row.pageNumber ?? null,
    remark: row.remark ?? null,
    isRenovation: row.isRenovation,
    isInactive: row.isInactive,
    project: row.project?.id ? { id: row.project.id } : undefined,
    surveyor: row.surveyor?.id ? { id: row.surveyor.id } : undefined,
    detailLots: (row.detailLots ?? []).map((lot) => {
      const base = mapDetailLotLineToPayload(lot);
      if (lot.id && isValidId(lot.id)) {
        return { ...base, id: lot.id, version: lot.version };
      }
      return base;
    }),
    secondaryWorkNatures: mapSecondaryWorkNatureLinesToPayload(row.secondaryWorkNatures),
  };
}

export function buildDeleteBody(row: U3139MinutesFormRow) {
  return { id: row.id, version: row.version };
}

export const doInitLU = (axios: AxiosInstance) => axios.get(LogicalUnitEndpoints.INITLU);

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) =>
  axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = forceRefresh ? { b: U2000GenerateUrlUUID() } : {};
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, data: ReturnType<typeof mapFormRowToCreatePayload>) =>
  axios.post(LogicalUnitEndpoints.CREATE, data);

export const doUpdateData = (axios: AxiosInstance, id: string | number, data: ReturnType<typeof mapFormRowToUpdatePayload>) =>
  axios.post(LogicalUnitEndpoints.UPDATE(id), data);

export const doDeleteData = (axios: AxiosInstance, id: string | number, data: { id: string; version?: number }) =>
  axios.post(LogicalUnitEndpoints.DELETE(id), data);

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => axios.get(LogicalUnitEndpoints.AUDITS(id));

export const doInitCreateMinute = (axios: AxiosInstance, body: U3139InitCreateMinuteRequest) =>
  axios.post(LogicalUnitEndpoints.INITCREATEMINUTE, body);

export type U3139FindCadastreCriterias = { fieldName: string; value: unknown }[];

export type U3139FindSearchCadastreCriterias = { fieldName: string; value: unknown }[];

export type U3139FindEmployeeCriterias = { fieldName: string; value: unknown }[];

export const doFindCadastres = (axios: AxiosInstance, criterias: U3139FindCadastreCriterias, pageSize?: number) =>
  axios.post(LogicalUnitEndpoints.FINDCADASTRES, criterias, { params: pageSize != null ? { pageSize } : {} });

export const doFindSearchCadastres = (axios: AxiosInstance, criterias: U3139FindSearchCadastreCriterias) =>
  axios.post(LogicalUnitEndpoints.FINDSEARCHCADASTRES, criterias);

export const doFindEmployee = (axios: AxiosInstance, criterias: U3139FindEmployeeCriterias, pageSize?: number) =>
  axios.post(LogicalUnitEndpoints.FINDEMPLOYEE, criterias, { params: pageSize != null ? { pageSize } : {} });

export function luHandleAppiError(_t: TranslateFn, NS: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { response?: { data?: { errorCode?: string; errorType?: string } } };
  const code = apiErr?.response?.data?.errorCode;
  if (code === "BU313901") {
    errorResult.genericErrors?.push({ title: _t("errorTitle", { ns: NS }), text: _t("errBU313901", { ns: NS }) });
  } else if (code === "BU313902") {
    errorResult.genericErrors?.push({ title: _t("errorTitle", { ns: NS }), text: _t("errBU313902", { ns: NS }) });
  } else if (code === "BU313903") {
    errorResult.genericErrors?.push({ title: _t("errorTitle", { ns: NS }), text: _t("errBU313903", { ns: NS }) });
  } else if (code === "BU313904") {
    errorResult.genericErrors?.push({ title: _t("errorTitle", { ns: NS }), text: _t("errBU313904", { ns: NS }) });
  } else if (code === "BU313905") {
    errorResult.genericErrors?.push({ title: _t("errorTitle", { ns: NS }), text: _t("errBU313905", { ns: NS }) });
  } else if (code === "BU313906") {
    errorResult.genericErrors?.push({ title: _t("errorTitle", { ns: NS }), text: _t("errBU313906", { ns: NS }) });
  } else if (code === "BU313907") {
    errorResult.fieldErrors = errorResult.fieldErrors ?? {};
    errorResult.fieldErrors.secondaryWorkNatures = _t("errBU313907", { ns: NS });
  } else if (code === "BU313908") {
    errorResult.fieldErrors = errorResult.fieldErrors ?? {};
    errorResult.fieldErrors.secondaryWorkNatures = _t("errBU313908", { ns: NS });
  }
}

export function validateMinutesForm(row: U3139MinutesFormRow, t: TranslateFn, ns: string): Record<string, string> {
  const result: Record<string, string> = {};
  if (!row.minuteNumber?.trim()) result.minuteNumber = t("errMinuteNumberRequired", { ns });
  if (!row.clientName?.trim()) result.clientName = t("errClientNameRequired", { ns });
  if (!u3139ResolveWorkNatureId(row.workNature)) result.workNature = t("errWorkNatureRequired", { ns });
  if (!row.minuteDate) result.minuteDate = t("errMinuteDateRequired", { ns });
  const primaryId = u3139ResolveWorkNatureId(row.workNature);
  const secondaryIds = u3139CollectSecondaryWorkNatureIds(row.secondaryWorkNatures);
  if (secondaryIds.length !== (row.secondaryWorkNatures?.length ?? 0)) {
    result.secondaryWorkNatures = t("errSecondaryWorkNatureInvalid", { ns });
  } else if (new Set(secondaryIds).size !== secondaryIds.length) {
    result.secondaryWorkNatures = t("errBU313907", { ns });
  } else if (primaryId && secondaryIds.includes(primaryId)) {
    result.secondaryWorkNatures = t("errBU313908", { ns });
  }
  return result;
}

/** Libellés affichables des natures secondaires (grille liste — U2000StringArrayDisplay). */
export function u3139SecondaryWorkNatureListLabels(
  lines: U3139SecondaryWorkNatureLine[] | undefined,
  languageCode: string,
  workNatures?: U3139WorkNatureItem[],
): string[] {
  if (!lines?.length) return [];
  return lines
    .map((line) => u3139FormatWorkNatureOptionLabel(line.workNature, languageCode, workNatures))
    .filter(Boolean);
}

/** Libellés secondaires concaténés (export Excel, titre cellule, etc.). */
export function u3139FormatSecondaryWorkNaturesCell(
  lines: U3139SecondaryWorkNatureLine[] | undefined,
  languageCode: string,
  workNatures?: U3139WorkNatureItem[],
): string {
  const labels = u3139SecondaryWorkNatureListLabels(lines, languageCode, workNatures);
  return labels.length ? labels.join("; ") : "";
}

/** Volets secondaires avec famille devant chaque libellé (grille U3121). */
export function u3139FormatSecondaryWorkNaturesWithFamilyCell(
  lines: U3139SecondaryWorkNatureLine[] | undefined,
  languageCode: string,
  workNatures?: U3139WorkNatureItem[],
): string {
  if (!lines?.length) return "";
  const labels = lines
    .map((line) => u3139FormatWorkNatureWithFamilyLabel(line.workNature, languageCode, workNatures))
    .filter(Boolean);
  return labels.length ? labels.join("; ") : "";
}

export function saveMinutesForm(
  row: U3139MinutesFormRow,
  editMode: U2000FormStateEnumEditMode,
  axios: AxiosInstance,
) {
  if (editMode === U2000FormStateEnumEditMode.ADD) {
    return doCreateData(axios, mapFormRowToCreatePayload(row));
  }
  return doUpdateData(axios, row.id!, mapFormRowToUpdatePayload(row));
}
